import React, { useEffect, useState, useMemo } from 'react';
import Paper from '@mui/material/Paper';
import { TeamDropdown } from '../components/dropdowns/TeamDropdown';
import moment from 'moment-timezone';
import CallMetrics from '../components/Snapshot/Metrics/CallMetrics';
import CallMetricsCharts from '../components/Snapshot/Metrics/CallMetricsCharts';
import SnapshotDateTabs from '../components/Snapshot/SnapshotTabs/SnapshotDateTabs';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';
import {
  makeApiRequestCached,
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { IUserAtom, user } from '../../recoil/atoms/user';
import AgentMetrics from '../components/Snapshot/Metrics/AgentMetrics';
import { currentTeam } from '../../recoil/atoms/currentTeam';
import SnapshotDateDisplay from '../components/Snapshot/SnapshotDateDisplay';
import { getDateRanges } from '../components/Snapshot/utils/getDateRanges';
import SnapshotTitle from '../components/Snapshot/SnapshotTitle';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';

export default function BusinessSnapshot() {
  const [_starting, setStarting] = useState(moment());
  const [_ending, setEnding] = useState(moment());
  const [dateInterval, setDateInterval] = useState<string>('day');
  const [team, _setTeam] = useRecoilState(currentTeam);

  const dateIntervals = ['day', 'week', 'month', 'quarter'];
  const setDates = (localStart: any, localEnd: any, index: number) => {
    setStarting(localStart);
    setEnding(localEnd);
    setDateInterval(dateIntervals[index]);
  };
  const [selectedTeamId, setSelectedTeamId] = useState<number | string>(
    team?.id ?? 0
  );
  const userAtomLoadable = useRecoilValueLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const [
    _,
    callMetricsLoadable,
    setRefetchToggleForCallMetrics,
  ] = makeApiRequestCached(
    {
      method: 'post',
      stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint: endpointEnum.GET_CALL_METRICS,
      payloadJsonString: JSON.stringify({
        apikey: userAtomContents?.apikey,
        id: selectedTeamId,
        type: selectedTeamId ? 'team' : 'business',
        interval: dateInterval,
        businessid: userAtomContents?.businessid,
        source: 'call-metrics',
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data.charts ?? cached.current
  );

  useEffect(() => {
    setRefetchToggleForCallMetrics({});
  }, []);

  const businessUsersLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    })
  );

  const hasCallTranscriptionProduct = useMemo(() => {
    return (businessUsersLoadable?.contents?.response?.data?.result ?? []).some(
      (user: any) => user.hasPdCallTranscription === 1
    );
  }, [businessUsersLoadable]);

  const dateRanges = useMemo(() => {
    return getDateRanges(callMetricsLoadable?.contents?.response?.data?.charts);
  }, [callMetricsLoadable?.contents?.response?.data?.charts]);

  return (
    <Paper>
      <TeamDropdown isSnapshot={true} setSelectedTeamId={setSelectedTeamId} />
      <SnapshotDateTabs onSetDates={setDates}>
        <div style={{ textAlign: 'center' }}>
          <SnapshotDateDisplay dateRanges={dateRanges} />
          <CallMetrics
            previousRange={dateRanges?.previousRange}
            callMetrics={callMetricsLoadable?.contents?.response?.data?.charts}
            loading={callMetricsLoadable?.state === 'loading'}
            hasCallTranscriptionProduct={hasCallTranscriptionProduct}
          />
          <CallMetricsCharts
            callMetrics={callMetricsLoadable?.contents?.response?.data?.charts}
            loading={callMetricsLoadable?.state === 'loading'}
          />
          <div style={{ margin: '36px 0 18px' }}>
            <SnapshotTitle title={'Agent Metrics'} />
          </div>
          <AgentMetrics
            previousRange={dateRanges?.previousRange}
            callMetricsLoadable={callMetricsLoadable}
            loading={callMetricsLoadable?.state === 'loading'}
          />
        </div>
      </SnapshotDateTabs>
    </Paper>
  );
}
